import React, { useState, useEffect, useMemo } from "react";

import { Grid, IconButton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import SkeletonComponent from "Components/Generic/Skeleton";
import { useTranslation } from "react-i18next";
import { FiTrash2, FiPlusCircle } from "react-icons/fi";
import { useSpring, animated } from "react-spring";
import useMeasure from "react-use-measure";
import { ContactListKeys } from "Util/queries";
import * as yup from "yup";

import Autosuggest from "../../../../../../../../../components/Form/AutoSuggest";
import Button, { Color } from "../../../../../../../../../components/Form/Button";
import Input from "../../../../../../../../../components/Form/Input";
import PhoneInput from "../../../../../../../../../components/Form/PhoneInput";
import UITable from "../../../../../../../../../components/Generic/UITable";
import { useAppState } from "../../../../../../../../../context/StateContext";
import { Contact } from "../../../../../../../../../models/contact";
import ContactService from "../../../../../../../../../services/contact.service";
import useStyles from "./styles";

interface Props {
  isOnViewMode: boolean;
  contactsList?: Contact[];
  errorMessage?: string;
  updateContactsOnWarehouse: (contacts: Contact[]) => void;
  isFetching?: boolean;
}

const Contacts = ({
  isOnViewMode = false,
  contactsList,
  errorMessage,
  updateContactsOnWarehouse,
  isFetching = false,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const [ref, bounds] = useMeasure();
  const { currentUser } = useAppState();

  const [name, setName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [mobilePhone, setMobilePhone] = useState<string>("");
  const [position, setPosition] = useState<string>("");

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact>();

  const [isShowingForm, setIsShowingForm] = useState<boolean>(false);
  const [isShowingAutoSuggest, setIsShowingAutoSuggest] = useState<boolean>(false);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const getErrorByField = (field: string) => errors?.find((error) => error.path === field)?.message;

  const formContentAnimatedStyle = useSpring({
    height: isShowingForm ? bounds.height : 0,
    marginTop: 16,
  });

  const schema = yup.object().shape({
    firstName: yup.string().required(translate("thisFieldIsRequired")),
    lastName: yup.string(),
    email: yup.string().required(translate("thisFieldIsRequired")),
    mobilePhone: yup.string().required(translate("thisFieldIsRequired")),
    position: yup.string().required(translate("thisFieldIsRequired")),
  });

  const handleValidationSuccess = (contact: Contact): boolean => {
    schema.validateSync(
      {
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        mobilePhone: contact.mobilePhone,
        position: contact.position,
      },
      { abortEarly: false }
    );
    setErrors([]);

    return true;
  };

  const handleValidationError = (error: any): boolean => {
    if (error instanceof yup.ValidationError) setErrors(error?.inner);

    return false;
  };

  const isValid = (contact: Contact) => {
    try {
      return handleValidationSuccess(contact);
    } catch (error) {
      return handleValidationError(error);
    }
  };

  const updateContactList = (contact: Contact) => {
    if (updateContactsOnWarehouse)
      updateContactsOnWarehouse([...contacts, { ...contact, companyId: currentUser?.companyId }]);
    setContacts((state) => state && [...state, contact]);
  };

  const handelAddContact = () => {
    const contact = {
      firstName: name,
      lastName,
      email,
      mobilePhone,
      position,
    };

    if (isValid(contact)) {
      updateContactList(contact);
      handleClearFields();
      setIsShowingForm(false);
    }
  };

  const handleDeleteContact = (index: number | string) => {
    const rowIndex = Number(index);
    if (Number.isNaN(rowIndex) || rowIndex < 0 || rowIndex >= contacts.length) return;

    const nextContacts = contacts.filter((_, contactIndex) => contactIndex !== rowIndex);
    setContacts(nextContacts);
    updateContactsOnWarehouse(nextContacts);
  };

  const handleClearFields = () => {
    setName("");
    setLastName("");
    setEmail("");
    setMobilePhone("");
    setPosition("");
  };

  const renderActionColumn = (index: number) => (
    <IconButton disabled={isOnViewMode || isFetching} onClick={() => handleDeleteContact(index)}>
      <FiTrash2 size={18} color="#f04438" />
    </IconButton>
  );

  const getContactOptionLabel = (contact: Contact): string => {
    return `${contact?.firstName || ""} ${contact?.lastName || ""} ${
      contact?.email ? `- ${contact?.email}` : ""
    }`;
  };

  const { data: internalContactList, isLoading: isLoadingContacts } = useQuery({
    queryKey: ContactListKeys.getAll(["not.customer", currentUser?.companyId]),
    queryFn: async () => {
      const response = await ContactService.getAll({
        companyId: currentUser?.companyId,
        customerId: "null",
        userId: "null",
      });

      if (response && response.data) return response.data;
    },
    enabled: !!currentUser?.companyId && isShowingAutoSuggest,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (contactsList) setContacts(contactsList);
  }, [contactsList]);

  const filteredContactList: Contact[] = useMemo(
    () =>
      internalContactList?.filter(
        (contact) =>
          contact?.firstName?.trim() !== "" &&
          !contactsList?.map((branchContact) => branchContact.id).includes(contact.id)
      ) ?? [],
    [internalContactList]
  );

  return (
    <div>
      <header className="d-flex align-items-center justify-content-between">
        <div className="d-flex flex-column">
          <span className={classes.contactTitle}>
            {isShowingForm ? translate("addNewContact") : translate("contacts")}
          </span>
          {errorMessage && <span style={{ color: "#f04438" }}>*{errorMessage}</span>}
        </div>
        <div>
          {!isOnViewMode && (
            <div className={classes.actionsWrapper}>
              <Button
                data-cy="addButtonForm"
                customColor={Color.PRIMARY}
                onClick={() => {
                  setIsShowingForm(!isShowingForm);
                  setIsShowingAutoSuggest(false);
                }}
                disabled={isOnViewMode || isFetching}
              >
                {!isShowingForm && <FiPlusCircle size={18} className="mr-2" />}
                {isShowingForm ? translate("hideForm") : translate("createNewContact")}
              </Button>
              <Button
                className={isShowingAutoSuggest ? classes.hideFormButton : ""}
                data-cy="addExistentContact"
                customColor={Color.SECONDARY}
                onClick={() => {
                  setIsShowingAutoSuggest(!isShowingAutoSuggest);
                  setIsShowingForm(false);
                }}
                disabled={isOnViewMode || isFetching}
              >
                {isShowingAutoSuggest ? translate("hideInput") : translate("addAnExistentContact")}
              </Button>
            </div>
          )}
        </div>
      </header>
      {isShowingForm && (
        <animated.div style={formContentAnimatedStyle}>
          <Grid
            container
            direction="row"
            alignItems="flex-start"
            spacing={2}
            data-cy="formToAddNewContact"
            ref={ref}
          >
            <Grid item md={6} lg={6}>
              <Input
                label={translate("firstName")}
                name="firstName"
                placeholder={translate("firstName")}
                required
                error={!!getErrorByField("firstName")}
                errorMessage={getErrorByField("firstName")}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Grid>
            <Grid item md={6} lg={6}>
              <Input
                label={translate("lastName")}
                name="lastName"
                placeholder={translate("lastName")}
                error={!!getErrorByField("lastName")}
                errorMessage={getErrorByField("lastName")}
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </Grid>
            <Grid item md={4} lg={4}>
              <Input
                label={translate("email")}
                name="email"
                placeholder="email@email.com"
                required
                error={!!getErrorByField("email")}
                errorMessage={getErrorByField("email")}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Grid>
            <Grid item md={4} lg={4}>
              <PhoneInput
                data-cy="phoneInput"
                name="phone"
                defaultCountry="AU"
                international
                label={translate("mobile")}
                value={mobilePhone}
                placeholder="61430111900"
                required
                error={!!getErrorByField("mobilePhone")}
                errorMessage={getErrorByField("mobilePhone")}
                onChange={(value) => setMobilePhone(value)}
              />
            </Grid>
            <Grid item md={4} lg={4}>
              <Input
                label={translate("position")}
                name="position"
                placeholder={translate("position")}
                required
                error={!!getErrorByField("position")}
                errorMessage={getErrorByField("position")}
                value={position}
                onChange={(event) => setPosition(event.target.value)}
              />
            </Grid>
            <Grid item md={12} lg={12} container justifyContent="flex-end">
              <Button
                data-cy="addContactButton"
                customColor={Color.SECONDARY}
                onClick={handelAddContact}
              >
                <FiPlusCircle size={18} className="mr-2" />
                {translate("add")}
              </Button>
            </Grid>
          </Grid>
        </animated.div>
      )}
      {isShowingAutoSuggest && (
        <Grid
          container
          direction="row"
          alignItems="flex-end"
          justifyContent="flex-end"
          md={6}
          lg={6}
          spacing={2}
        >
          <Grid item md={9} lg={9}>
            <SkeletonComponent isLoading={isLoadingContacts}>
              <Autosuggest
                disabled={isOnViewMode || isFetching}
                label={translate("contact")}
                placeholder={translate("selectAContact")}
                options={filteredContactList}
                data-cy="contacts"
                inputValue={selectedContact ? getContactOptionLabel(selectedContact) : ""}
                getOptionLabel={(contact: Contact) => getContactOptionLabel(contact)}
                getOptionSelected={(option: Contact, value: Contact) => option.id === value.id}
                onChange={(contact: Contact) => {
                  if (contact) setSelectedContact(contact);
                }}
              />
            </SkeletonComponent>
          </Grid>
          <Grid item container md={3} lg={3} justifyContent="flex-end">
            <Button
              className={classes.button}
              customColor={Color.SECONDARY}
              data-cy="cancelAddExistentContact"
              onClick={() => {
                if (selectedContact) updateContactList(selectedContact);
              }}
            >
              {translate("add")}
            </Button>
          </Grid>
        </Grid>
      )}
      <section className="pb-3">
        <UITable
          titleTable=""
          data={contacts}
          actionColumn={renderActionColumn}
          checkField={false}
          isOnViewMode={isOnViewMode}
          isActionColumnOnEnd={false}
          columns={[
            {
              title: translate("firstName"),
              columnName: "firstName",
              spacingColumns: ".5fr",
            },
            {
              title: translate("email"),
              columnName: "email",
              spacingColumns: ".75fr",
            },
            {
              title: translate("phone"),
              columnName: "mobilePhone",
              spacingColumns: "1fr",
              formatComponent: (value: string) => (
                <PhoneInput
                  disabled
                  value={value}
                  placeholder="61430111900"
                  className={classes.phoneInputColumn}
                />
              ),
            },
            {
              title: translate("position"),
              columnName: "position",
              spacingColumns: ".5fr",
            },
          ]}
        />
      </section>
    </div>
  );
};

export default Contacts;
